/**
 * Helpers partagés — thème NOMBRES 3ème (attendus 201-204, desc d10-d12 bloc A).
 *
 * Repris des conventions 4e (maths/4eme/nombres/nombres.js) : tout par
 * construction, exposants Unicode à l'AFFICHAGE seulement (jamais tapés),
 * développement décimal EXACT par chaînes (aucun flottant), toolkit rationnel
 * entier exact. Les réponses saisies utilisent le signe ASCII « - ».
 */

// ── Typographie mathématique (KaTeX) ─────────────────────────────────
// Les exposants Unicode (« 10⁻³ ») ont été remplacés par du KaTeX : ils
// n'existent pas pour toutes les valeurs, ne se composent pas (10⁻³ × 10⁵ reste
// du texte plat) et rendent mal les fractions. Voir FORMATS_TANOED §0.5bis.
//
// Convention de ce thème : les helpers produisent des FRAGMENTS LaTeX sans les
// `$` (suffixe `Frag`), ce qui permet de composer une expression complète
// (`${puissFrag(10, a)} \\times ${puissFrag(10, b)}`) puis de l'emballer UNE
// seule fois avec `tex()` — plutôt que de juxtaposer plusieurs îlots KaTeX.

/** Fragment LaTeX d'une puissance : (10, -3) → « 10^{-3} », ('(-4)', 2) → « (-4)^{2} ». */
export const puissFrag = (base, n) => `${base}^{${n}}`;

/** Fragment LaTeX d'une fraction p/q, signe sorti devant la barre. */
export const fracFrag = (p, q) => {
    if (Number(q) === 1) return String(p);
    const signe = (Number(p) < 0) !== (Number(q) < 0) ? '-' : '';
    return `${signe}\\dfrac{${Math.abs(Number(p))}}{${Math.abs(Number(q))}}`;
};

/** Fragment LaTeX d'un rationnel { p, q } du toolkit ci-dessous. */
export const ratFrag = (r) => fracFrag(r.p, r.q);

/** Fragment LaTeX d'une puissance de fraction : (3, 4, -2) → « \left(\dfrac{3}{4}\right)^{-2} ». */
export const fracPuissFrag = (p, q, n) =>
    `\\left(${fracFrag(p, q)}\\right)^{${n}}`;

/**
 * Fragment LaTeX d'un décimal français : « 0,125 » → « 0{,}125 ».
 * Sans les accolades, KaTeX traite la virgule comme une ponctuation et insère
 * une espace après (« 0, 125 »).
 */
export const decFrag = (s) => String(s).replace(/,/g, '{,}');

// ── Développement décimal EXACT (pas de flottant) ────────────────────

/** Écriture décimale (chaîne) d'une puissance de 10 : dec10(-4) = « 0,0001 ». */
export const dec10 = (n) => {
    if (n === 0) return '1';
    if (n > 0) return '1' + '0'.repeat(n);
    return '0,' + '0'.repeat(-n - 1) + '1';
};

/**
 * Développe « mant × 10^e » en écriture décimale EXACTE (chaîne, virgule FR,
 * sans zéros superflus). mant = chaîne FR (« 6,54 », « -3,2 »).
 */
export const developpe = (mant, e) => {
    let s = String(mant).trim();
    const neg = s.startsWith('-');
    if (neg) s = s.slice(1);
    const [ent, dec = ''] = s.split(',');
    const digits = ent + dec;
    let point = ent.length + e;
    let out;
    if (point <= 0) out = '0,' + '0'.repeat(-point) + digits;
    else if (point >= digits.length) out = digits + '0'.repeat(point - digits.length);
    else out = digits.slice(0, point) + ',' + digits.slice(point);
    if (out.includes(',')) out = out.replace(/0+$/, '').replace(/,$/, '');
    out = out.replace(/^0+(?=\d)/, '');
    return (neg ? '-' : '') + out;
};

/** Groupe les milliers par espaces (affichage) : « 1234567,8 » → « 1 234 567,8 ». */
export const grp = (s) => {
    const str = String(s);
    const neg = str.startsWith('-');
    const x = neg ? str.slice(1) : str;
    const [ent, dec] = x.split(',');
    const g = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (neg ? '-' : '') + (dec !== undefined ? `${g},${dec}` : g);
};

// ── Carrés parfaits et racines ───────────────────────────────────────

export const estCarreParfait = (n) => {
    if (!Number.isInteger(n) || n < 0) return false;
    const r = Math.round(Math.sqrt(n));
    return r * r === n;
};

/** Racine carrée entière d'un carré parfait (Math.round anti-flottant). */
export const racineEntiere = (n) => Math.round(Math.sqrt(n));

// ── Toolkit rationnel EXACT (mêmes conventions que le 4e) ────────────
// Un rationnel = { p, q } avec q > 0, TOUJOURS réduit ; signe porté par p.

export const pgcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };

export const rat = (p, q = 1) => {
    if (q < 0) { p = -p; q = -q; }
    const g = pgcd(p, q) || 1;
    return { p: p / g, q: q / g };
};
export const ratAdd = (a, b) => rat(a.p * b.q + b.p * a.q, a.q * b.q);
export const ratSub = (a, b) => rat(a.p * b.q - b.p * a.q, a.q * b.q);
export const ratMul = (a, b) => rat(a.p * b.p, a.q * b.q);
export const ratDiv = (a, b) => rat(a.p * b.q, a.q * b.p);
export const ratInv = (a) => rat(a.q, a.p);
export const ratOpp = (a) => ({ p: -a.p, q: a.q });
export const ratEq = (a, b) => a.p * b.q === b.p * a.q;
export const ratCmp = (a, b) => a.p * b.q - b.p * a.q;

/** Puissance entière d'un rationnel (n ≥ 0). */
export const ratPow = (a, n) => { let r = rat(1, 1); for (let i = 0; i < n; i++) r = ratMul(r, a); return r; };

/**
 * Écriture décimale FR EXACTE d'un rationnel si le développement est fini
 * (dénominateur 2^a·5^b), sinon null. Signe ASCII « - ».
 */
export const decExact = (r) => {
    let q = r.q; let a = 0; let b = 0;
    while (q % 2 === 0) { q /= 2; a++; }
    while (q % 5 === 0) { q /= 5; b++; }
    if (q !== 1) return null;
    const k = Math.max(a, b);
    const num = r.p * (10 ** k) / r.q;
    if (k === 0) return String(num);
    const neg = num < 0; const abs = String(Math.abs(num)).padStart(k + 1, '0');
    const ent = abs.slice(0, abs.length - k);
    const dec = abs.slice(abs.length - k).replace(/0+$/, '');
    return (neg ? '-' : '') + (dec ? `${ent},${dec}` : ent);
};

/** Remplace le moins ASCII par le moins typographique « − » (AFFICHAGE seul). */
export const aff = (s) => String(s).replace(/-/g, '−');

// ── Puissances entières exactes ──────────────────────────────────────

/** base^n exact (n ≥ 0, résultat < 2^53 pour nos tirages). */
export const pow = (base, n) => { let r = 1; for (let i = 0; i < n; i++) r *= base; return r; };
